/**
 * Barge-In Refinement — graduated speech-during-TTS handling.
 *
 * Phase 2 of voice turn-taking (#170).
 *
 * Instead of immediately killing TTS when VAD fires, barge-in goes through
 * stages:
 *
 * 1. **Duck** — Speech detected during TTS → duck TTS volume immediately.
 *    This is instant and happens on every VAD speech start.
 *
 * 2. **Candidate** — The speech is a barge-in candidate. We wait for either:
 *    a. A real STT segment (non-empty transcript) → promote to real interrupt
 *    b. Sustained speech past a threshold → promote to real interrupt
 *    c. Speech ends quickly with no transcript → false alarm, resume TTS
 *
 * 3. **Promoted** — Real interrupt confirmed:
 *    - Abort the active run via chat.abort
 *    - Suppress/discard current and pending TTS for this run
 *    - Begin collecting a new voice turn
 *
 * This avoids false interrupts from coughs, background noise, and brief
 * utterances that don't constitute real user input.
 */

import { remoteLog } from '../utils/remoteLog';

// ── Configuration ──

const DEFAULTS = {
  /** Minimum sustained speech (ms) before auto-promoting without STT */
  SUSTAINED_SPEECH_MS: 800,
  /** Maximum time (ms) to wait for promotion before auto-resuming */
  CANDIDATE_TIMEOUT_MS: 3000,
} as const;

const LS_PREFIX = 'talkyn-bargein-';
const LS_KEYS = {
  SUSTAINED_SPEECH_MS: `${LS_PREFIX}sustained-ms`,
  CANDIDATE_TIMEOUT_MS: `${LS_PREFIX}candidate-timeout-ms`,
} as const;

function readInt(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
  } catch { /* ignore */ }
  return fallback;
}

// ── Types ──

export type BargeInState =
  | 'inactive'      // No TTS playing, barge-in not applicable
  | 'monitoring'    // TTS is playing, watching for speech
  | 'ducked'        // Speech detected, TTS ducked, waiting for promotion
  | 'promoted';     // Real interrupt confirmed

export interface BargeInCallbacks {
  /** Called to duck TTS volume */
  onDuck: () => void;
  /** Called to restore TTS volume (false alarm) */
  onUnduck: () => void;
  /** Called when barge-in is promoted to real interrupt */
  onPromote: (reason: string) => void;
}

// ── Barge-In Manager ──

export class BargeInManager {
  private callbacks: BargeInCallbacks;
  private _state: BargeInState = 'inactive';
  private speechStartTime = 0;
  private sustainedTimer: ReturnType<typeof setTimeout> | null = null;
  private candidateTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

  // Thresholds
  private readonly sustainedSpeechMs: number;
  private readonly candidateTimeoutMs: number;

  constructor(callbacks: BargeInCallbacks) {
    this.callbacks = callbacks;
    this.sustainedSpeechMs = readInt(
      LS_KEYS.SUSTAINED_SPEECH_MS,
      DEFAULTS.SUSTAINED_SPEECH_MS,
    );
    this.candidateTimeoutMs = readInt(
      LS_KEYS.CANDIDATE_TIMEOUT_MS,
      DEFAULTS.CANDIDATE_TIMEOUT_MS,
    );
  }

  /** Current barge-in state */
  get state(): BargeInState {
    return this._state;
  }

  /** Whether we're in a ducked candidate state */
  get isDucked(): boolean {
    return this._state === 'ducked';
  }

  /** Whether barge-in has been promoted to a real interrupt */
  get isPromoted(): boolean {
    return this._state === 'promoted';
  }

  /**
   * Notify that TTS playback has started.
   * Transitions to monitoring state.
   */
  ttsStarted(): void {
    if (this._state === 'promoted') return; // Already interrupted
    this._state = 'monitoring';
    remoteLog('bargeIn', 'tts_started', { state: this._state });
  }

  /**
   * Notify that TTS playback has stopped naturally.
   * Returns to inactive.
   */
  ttsStopped(): void {
    this.clearTimers();
    this._state = 'inactive';
    remoteLog('bargeIn', 'tts_stopped', {});
  }

  /**
   * Notify that speech was detected (VAD onSpeechStart).
   *
   * If TTS is playing (monitoring), duck immediately and enter candidate state.
   * If TTS is not playing, this is a no-op (normal speech, handled by turn arbiter).
   */
  speechDetected(): void {
    if (this._state !== 'monitoring') {
      remoteLog('bargeIn', 'speech_ignored', { state: this._state });
      return;
    }

    // Duck TTS immediately
    this._state = 'ducked';
    this.speechStartTime = Date.now();
    this.callbacks.onDuck();

    remoteLog('bargeIn', 'ducked', {
      sustainedThreshold: this.sustainedSpeechMs,
    });

    // Start sustained speech timer — auto-promote if speech continues
    this.sustainedTimer = setTimeout(() => {
      this.sustainedTimer = null;
      if (this._state === 'ducked') {
        this.promote('sustained_speech');
      }
    }, this.sustainedSpeechMs);

    // Safety timeout — if neither promotion nor resume happens
    this.candidateTimeoutTimer = setTimeout(() => {
      this.candidateTimeoutTimer = null;
      if (this._state === 'ducked') {
        // Timeout without promotion or speech end — promote as safety
        this.promote('candidate_timeout');
      }
    }, this.candidateTimeoutMs);
  }

  /**
   * Notify that speech ended (VAD onSpeechEnd) while in ducked state.
   *
   * If we haven't been promoted yet, this is a potential false alarm.
   * We don't immediately resume — we wait for the STT result.
   * If STT returns empty, then we resume (handled by sttResult).
   * If STT returns text, we promote (handled by sttResult).
   */
  speechEnded(): void {
    if (this._state !== 'ducked') return;

    // Cancel the sustained speech timer since speech stopped
    if (this.sustainedTimer) {
      clearTimeout(this.sustainedTimer);
      this.sustainedTimer = null;
    }

    const speechDuration = Date.now() - this.speechStartTime;
    remoteLog('bargeIn', 'speech_ended_while_ducked', {
      durationMs: speechDuration,
    });
    // Stay in ducked state — wait for STT result to decide
  }

  /**
   * Notify of an STT result while in ducked candidate state.
   *
   * @param hasContent - Whether the STT returned meaningful text
   */
  sttResult(hasContent: boolean): void {
    if (this._state !== 'ducked') return;

    if (hasContent) {
      // Real speech content → promote to interrupt
      this.promote('stt_segment');
    } else {
      // Empty STT result → false alarm, resume TTS
      this.resume('empty_stt');
    }
  }

  /**
   * Force promote (external trigger, e.g., user taps stop).
   */
  forcePromote(reason: string): void {
    if (this._state === 'ducked' || this._state === 'monitoring') {
      this.promote(reason);
    }
  }

  /**
   * Reset to inactive (e.g., when voice pipeline stops).
   */
  reset(): void {
    this.clearTimers();
    this._state = 'inactive';
  }

  /**
   * Clean up timers.
   */
  destroy(): void {
    this.clearTimers();
    this._state = 'inactive';
  }

  // ── Private ──

  private promote(reason: string): void {
    this.clearTimers();
    this._state = 'promoted';
    const speechDuration = Date.now() - this.speechStartTime;

    remoteLog('bargeIn', 'promoted', {
      reason,
      speechDurationMs: speechDuration,
    });

    this.callbacks.onPromote(reason);
  }

  private resume(reason: string): void {
    this.clearTimers();
    const speechDuration = Date.now() - this.speechStartTime;

    remoteLog('bargeIn', 'resumed', {
      reason,
      speechDurationMs: speechDuration,
    });

    this._state = 'monitoring';
    this.callbacks.onUnduck();
  }

  private clearTimers(): void {
    if (this.sustainedTimer) {
      clearTimeout(this.sustainedTimer);
      this.sustainedTimer = null;
    }
    if (this.candidateTimeoutTimer) {
      clearTimeout(this.candidateTimeoutTimer);
      this.candidateTimeoutTimer = null;
    }
  }
}
